import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { useMaterialUIController } from "context";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    AreaChart,
    Area,
} from "recharts";
import { request } from "app/network";

// Interfaces matching Backend DTOs
interface DailySteps {
    date: string; // "yyyy-MM-dd"
    day?: string; // "MON", "TUE" etc (Optional, computed if missing)
    steps?: number; // UI expects this
    stepCount?: number; // API returns this
}

interface MonthlyPoints {
    month: string; // "yyyy.MM"
    earned: number;
    used: number;
}

interface PointHistory {
    id: number;
    date: string;
    type: string; // "적립" or "사용"
    description: string;
    point: number;
    status: string;
}

interface UserActivityStats {
    userId: string;
    userName: string;
    totalSteps: number;
    currentPoints: number;
    todayPointsEarned: number;
    monthlyPointsEarned: number;
    todayPointsUsed: number;
    monthlyPointsUsed: number;
    // Backend DTO optional properties
    totalStepsToday?: number;
    earnedPointsToday?: number;
    usedPointsToday?: number;
    earnedPointsMonth?: number;
    usedPointsMonth?: number;

    weeklySteps: DailySteps[];
    monthlyPoints: MonthlyPoints[];
    recentPointHistory: PointHistory[];
}

interface UserActivityDialogProps {
    open: boolean;
    onClose: () => void;
    userId: string;
}

function UserActivityDialog({ open, onClose, userId }: UserActivityDialogProps) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const theme = useTheme();
    const [stats, setStats] = useState<UserActivityStats | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [focusBar, setFocusBar] = useState<number | null>(null);

    // Fetch Data
    useEffect(() => {
        if (open && userId) {
            setLoading(true);
            request("User.getActivityStats", {
                paramz: {},
                pathParams: { id: userId },
            })
                .then((response) => {
                    if (response && response.data) {
                        const data = response.data;

                        // DATA MAPPING: Backend DTO -> Frontend Interface
                        // The backend returns properties like 'TotalStepsToday', 'EarnedPointsMonth' which differ from
                        // the frontend interface 'totalSteps', 'monthlyPointsEarned'. We map them here.

                        // 1. Map Top-Level Stats
                        // Use 'in' operator or check undefined to prioritize Backend values if present
                        data.totalSteps = data.totalSteps ?? data.totalStepsToday ?? 0;
                        data.currentPoints = data.currentPoints ?? 0; // Usually matches
                        data.todayPointsEarned = data.todayPointsEarned ?? data.earnedPointsToday ?? 0;
                        data.todayPointsUsed = data.todayPointsUsed ?? data.usedPointsToday ?? 0;
                        data.monthlyPointsEarned = data.monthlyPointsEarned ?? data.earnedPointsMonth ?? 0;
                        data.monthlyPointsUsed = data.monthlyPointsUsed ?? data.usedPointsMonth ?? 0;

                        // 2. Map Weekly Steps (Array)
                        // data.weeklySteps might be undefined if list is empty
                        if (data.weeklySteps) {
                            data.weeklySteps = data.weeklySteps.map((item: any) => {
                                const dateObj = new Date(item.date);
                                const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                return {
                                    ...item,
                                    steps: item.steps ?? item.stepCount ?? 0,
                                    day: item.day ?? days[dateObj.getDay()]
                                };
                            });
                        } else {
                            data.weeklySteps = [];
                        }

                        // 3. Map Monthly Points (Array) - similar check
                        if (!data.monthlyPoints) {
                            data.monthlyPoints = [];
                        }

                        // 4. Map History (Array) - Map Backend DTO to Frontend Interface
                        if (data.recentPointHistory) {
                            data.recentPointHistory = data.recentPointHistory.map((item: any) => ({
                                ...item,
                                point: item.point ?? item.amount ?? 0,
                                // Map Backend "EARN"/"USE" to Frontend Display "적립"/"사용"
                                type: item.type === "EARN" ? "적립" : (item.type === "USE" ? "사용" : item.type),
                                status: item.status ?? "완료"
                            }));
                        } else {
                            data.recentPointHistory = [];
                        }

                        console.log("Mapped Activity Stats:", data);
                        setStats(data);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch activity stats:", err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setStats(null);
        }
    }, [open, userId]);

    // Theme Colors
    const cardBg = darkMode
        ? (theme?.palette?.background?.paper || '#2d2d2d')
        : (theme?.palette?.background?.paper || '#ffffff');
    const borderColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    const textColor = darkMode ? "white" : "dark";
    const subTextColor = darkMode ? "text" : "secondary";
    const isDarkMode = darkMode;



    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : "#f0f2f5",
                    borderRadius: 3,
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
                }
            }}
        >
            <DialogContent sx={{ p: 3 }}>
                {/* Header */}
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDBox display="flex" alignItems="center">
                        <MDBox
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "12px",
                                background: "linear-gradient(135deg, #FF0080 0%, #7928CA 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 2,
                                boxShadow: "0 4px 12px rgba(121, 40, 202, 0.3)"
                            }}
                        >
                            <Icon sx={{ color: "white", fontSize: "1.5rem" }}>analytics</Icon>
                        </MDBox>
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="bold" color={textColor}>
                                사용자 활동 통계
                            </MDTypography>
                            <MDTypography variant="button" color={subTextColor} fontWeight="regular">
                                {stats?.userName || userId}님의 최근 활동 내역입니다.
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        aria-label="close"
                        sx={{
                            color: isDarkMode ? "#ffffff !important" : "inherit"
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </MDBox>

                {loading ? (
                    <MDBox display="flex" justifyContent="center" p={5}>
                        <MDTypography>데이터 불러오는 중...</MDTypography>
                    </MDBox>
                ) : stats ? (
                    <>
                        {/* Summary Cards */}
                        <Grid container spacing={3} mb={3}>
                            {[
                                { title: "총 걸음 수", value: (stats?.totalSteps || 0).toLocaleString(), icon: "directions_walk", color: "info", gradient: "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)" },
                                { title: "현재 포인트", value: `${(stats?.currentPoints || 0).toLocaleString()} P`, icon: "monetization_on", color: "warning", gradient: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)" },
                                { title: "이번 달 적립", value: `${(stats?.monthlyPointsEarned || 0).toLocaleString()} P`, icon: "trending_up", color: "success", gradient: "linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)" },
                                { title: "이번 달 사용", value: `${(stats?.monthlyPointsUsed || 0).toLocaleString()} P`, icon: "shopping_cart", color: "error", gradient: "linear-gradient(135deg, #F44336 0%, #E91E63 100%)" }
                            ].map((card, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Card sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
                                        border: `1px solid ${borderColor}`,
                                        bgcolor: cardBg
                                    }}>
                                        <MDBox p={3} display="flex" alignItems="center">
                                            <MDBox
                                                width={48}
                                                height={48}
                                                borderRadius="50%"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    background: card.gradient,
                                                    mr: 2,
                                                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                <Icon sx={{ color: "white" }}>{card.icon}</Icon>
                                            </MDBox>
                                            <MDBox>
                                                <MDTypography variant="button" color={subTextColor} fontWeight="medium" sx={{ opacity: 0.7 }}>
                                                    {card.title}
                                                </MDTypography>
                                                <MDTypography variant="h4" fontWeight="bold" color={textColor}>
                                                    {card.value}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Charts Row */}
                        <Grid container spacing={3} mb={3}>
                            {/* Left: Weekly Steps */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)", bgcolor: cardBg, border: `1px solid ${borderColor}`, height: "100%" }}>
                                    <MDTypography variant="h6" fontWeight="bold" color={textColor} mb={3}>
                                        주간 걸음 수
                                    </MDTypography>
                                    <MDBox height={250} width="100%">
                                        <ResponsiveContainer>
                                            <BarChart data={stats?.weeklySteps || []} barSize={30}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#E0E0E0"} />
                                                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "white" : "#7B809A", fontSize: 12 }} dy={10} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "white" : "#7B809A", fontSize: 12 }} dx={-10} />
                                                <Tooltip
                                                    cursor={{ fill: "transparent" }}
                                                    contentStyle={{ backgroundColor: "#344767", color: "white", borderRadius: "8px", border: "none" }}
                                                    itemStyle={{ color: "white" }}
                                                />
                                                <defs>
                                                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#4facfe" />
                                                        <stop offset="100%" stopColor="#00f2fe" />
                                                    </linearGradient>
                                                </defs>
                                                <Bar dataKey="steps" radius={[6, 6, 6, 6]}>
                                                    {stats?.weeklySteps?.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill="url(#barGradient)"
                                                            onMouseEnter={() => setFocusBar(index)}
                                                            onMouseLeave={() => setFocusBar(null)}
                                                            style={{ filter: focusBar === index ? "brightness(1.1)" : "none", transition: "filter 0.3s ease" }}
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </MDBox>
                                </Card>
                            </Grid>

                            {/* Right: Point Trend (Monthly) */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)", bgcolor: cardBg, border: `1px solid ${borderColor}`, height: "100%" }}>
                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                        <MDTypography variant="h6" fontWeight="bold" color={textColor}>
                                            월별 포인트 현황
                                        </MDTypography>
                                        <MDBox display="flex" gap={2}>
                                            <MDBox width={8} height={8} borderRadius="50%" bgcolor="#43e97b" mr={1} />
                                            <MDTypography variant="caption" color={subTextColor} fontWeight="medium">적립</MDTypography>
                                            <MDBox width={8} height={8} borderRadius="50%" bgcolor="#ff512f" mr={1} />
                                            <MDTypography variant="caption" color={subTextColor} fontWeight="medium">사용</MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDBox height={250} width="100%">
                                        <ResponsiveContainer>
                                            <AreaChart data={stats?.monthlyPoints || []}>
                                                <defs>
                                                    <linearGradient id="colorEarned" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#43e97b" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#43e97b" stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id="colorUsed" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#ff512f" stopOpacity={0.8} />
                                                        <stop offset="95%" stopColor="#ff512f" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#E0E0E0"} />
                                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "white" : "#7B809A", fontSize: 12 }} dy={10} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "white" : "#7B809A", fontSize: 12 }} dx={-10} />
                                                <Tooltip
                                                    contentStyle={{ backgroundColor: "#344767", color: "white", borderRadius: "8px", border: "none" }}
                                                    itemStyle={{ color: "white" }}
                                                />
                                                <Area type="monotone" dataKey="earned" stroke="#43e97b" fillOpacity={1} fill="url(#colorEarned)" strokeWidth={3} />
                                                <Area type="monotone" dataKey="used" stroke="#ff512f" fillOpacity={1} fill="url(#colorUsed)" strokeWidth={3} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Bottom Row: Recent Point History */}
                        <Card sx={{ p: 3, boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)", bgcolor: cardBg, border: `1px solid ${borderColor}` }}>
                            <MDTypography variant="h6" fontWeight="bold" color={textColor} mb={2}>
                                최근 포인트 내역
                            </MDTypography>

                            <MDBox component="table" width="100%" sx={{ borderCollapse: "collapse" }}>
                                <MDBox component="thead">
                                    <MDBox component="tr" sx={{ borderBottom: `1px solid ${borderColor}` }}>
                                        <MDBox component="th" py={1.5} textAlign="left"><MDTypography variant="button" color={subTextColor} fontWeight="medium">날짜</MDTypography></MDBox>
                                        <MDBox component="th" py={1.5} textAlign="left"><MDTypography variant="button" color={subTextColor} fontWeight="medium">구분</MDTypography></MDBox>
                                        <MDBox component="th" py={1.5} textAlign="left"><MDTypography variant="button" color={subTextColor} fontWeight="medium">내역</MDTypography></MDBox>
                                        <MDBox component="th" py={1.5} textAlign="right"><MDTypography variant="button" color={subTextColor} fontWeight="medium">포인트</MDTypography></MDBox>
                                        <MDBox component="th" py={1.5} textAlign="center"><MDTypography variant="button" color={subTextColor} fontWeight="medium">상태</MDTypography></MDBox>
                                    </MDBox>
                                </MDBox>
                                <MDBox component="tbody">
                                    {stats?.recentPointHistory?.map((log) => (
                                        <MDBox component="tr" key={log.id} sx={{ borderBottom: `1px solid ${borderColor}`, "&:last-child": { borderBottom: "none" } }}>
                                            <MDBox component="td" py={2}>
                                                <MDTypography variant="body2" color={textColor} fontWeight="medium">{log.date}</MDTypography>
                                            </MDBox>
                                            <MDBox component="td" py={2}>
                                                <MDTypography variant="body2" color={textColor}>{log.type}</MDTypography>
                                            </MDBox>
                                            <MDBox component="td" py={2}>
                                                <MDTypography variant="body2" color={textColor}>{log.description}</MDTypography>
                                            </MDBox>
                                            <MDBox component="td" py={2} textAlign="right">
                                                <MDTypography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    color={log.type === "적립" ? "success" : "error"}
                                                >
                                                    {log.type === "적립" ? "+" : "-"}{(log.point || 0).toLocaleString()}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox component="td" py={2} textAlign="center">
                                                <MDBadge
                                                    badgeContent={log.status}
                                                    color={log.status === "완료" || log.status === "성공" ? "success" : "warning"}
                                                    variant="gradient"
                                                    size="sm"
                                                    container
                                                />
                                            </MDBox>
                                        </MDBox>
                                    ))}
                                </MDBox>
                            </MDBox>
                            {!stats?.recentPointHistory?.length && (
                                <MDBox textAlign="center" py={4}>
                                    <MDTypography variant="button" color="text">최근 내역이 없습니다.</MDTypography>
                                </MDBox>
                            )}
                        </Card>
                    </>
                ) : (
                    <MDBox display="flex" justifyContent="center" p={5}>
                        <MDTypography>데이터가 없습니다.</MDTypography>
                    </MDBox>
                )}
            </DialogContent>
        </Dialog>
    );
}



export default UserActivityDialog;
